import { PlusOutlined, PrinterOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Dropdown, Flex, Form, InputNumber, Modal, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { NoWms, useWmsWarehouse } from "./common";
import { printPdf } from "./packLabel";
import { usePoll } from "@/components/tabs/WorkTabs";

type Container = Schemas["Container"];
type Kind = Container["kind"];

/**
 * Gudang > Tote & cart (Plan §6.9): the totes single and batch picking fill
 * and the carts with numbered slots cluster picking fills, their labels, and
 * which task uses each.
 */
export function Containers() {
  const { t, formatNumber } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const { current, loading } = useWmsWarehouse();
  const [adding, setAdding] = useState<Kind | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [printing, setPrinting] = useState(false);
  const list = useQuery({
    queryKey: ["containers", current?.id],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/containers", { params: { path: { warehouseID: current!.id } } })),
    enabled: !!current,
    refetchInterval: usePoll(15_000),
  });
  const items = list.data?.items ?? [];
  const print = async (paper: "thermal" | "a4") => {
    setPrinting(true);
    try {
      printPdf(
        await unwrap(
          api.GET("/v1/warehouses/{warehouseID}/container-labels", {
            params: { path: { warehouseID: current!.id }, query: { ids: selected.join(","), paper } },
            parseAs: "blob",
          }),
        ),
      );
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setPrinting(false);
    }
  };

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.ct.title")}
        </Typography.Title>
        <Space wrap>
          <Dropdown
            disabled={selected.length === 0}
            menu={{
              items: [
                { key: "thermal", label: t("wms.pkg.printThermal") },
                { key: "a4", label: t("wms.pkg.printA4") },
              ],
              onClick: ({ key }) => void print(key as "thermal" | "a4"),
            }}
          >
            <Button icon={<PrinterOutlined />} loading={printing}>
              {t("wms.ct.print", { n: selected.length })}
            </Button>
          </Dropdown>
          {can("warehouse.manage") && (
            <>
              <Button icon={<PlusOutlined />} onClick={() => setAdding("cart")}>
                {t("wms.ct.addCart")}
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding("tote")}>
                {t("wms.ct.addTote")}
              </Button>
            </>
          )}
        </Space>
      </Flex>
      <Typography.Paragraph type="secondary">
        {t(current?.containerRequired ? "wms.ct.helpRequired" : "wms.ct.helpOptional")}
      </Typography.Paragraph>
      <Table<Container>
        rowKey="id"
        loading={list.isPending}
        dataSource={items}
        pagination={CLIENT_PAGING}
        rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys.map(String)) }}
        locale={{ emptyText: t("wms.ct.empty") }}
        columns={[
          { title: t("wms.ct.code"), dataIndex: "code", width: 160, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          {
            title: t("wms.ct.kind"),
            dataIndex: "kind",
            width: 160,
            render: (k: Kind, c) => (k === "cart" ? <Tag color="purple">{t("wms.ct.cartSlots", { n: c.slots })}</Tag> : <Tag color="blue">{t("wms.ct.tote")}</Tag>),
          },
          {
            title: t("common.status"),
            render: (_, c) =>
              c.taskId ? (
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/app/warehouse/tasks/${c.taskId}`)}>
                  {t("wms.ct.inTask", { task: c.taskNumber, who: c.taskAssignee || "—" })}
                </Button>
              ) : c.units > 0 ? (
                <Tag color="gold">{t("wms.ct.holding")}</Tag>
              ) : (
                <Tag color="success">{t("wms.ct.free")}</Tag>
              ),
          },
          { title: t("wms.ct.units"), dataIndex: "units", width: 110, align: "right", render: (n: number) => <span className="z-num">{formatNumber(n)}</span> },
        ]}
      />
      {current && <AddModal warehouseId={current.id} kind={adding} onClose={() => setAdding(null)} />}
    </>
  );
}

function AddModal({ warehouseId, kind, onClose }: { warehouseId: string; kind: Kind | null; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ count: number; slots: number }>();
  const add = useMutation({
    mutationFn: (v: { count: number; slots: number }) =>
      unwrap(api.POST("/v1/warehouses/{warehouseID}/containers", { params: { path: { warehouseID: warehouseId } }, body: { kind: kind!, count: v.count, slots: v.slots } })),
    onSuccess: (r) => {
      void message.success(t("wms.ct.added", { codes: r.codes.join(", ") }));
      void queryClient.invalidateQueries({ queryKey: ["containers"] });
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open={kind !== null}
      title={kind === "cart" ? t("wms.ct.addCart") : t("wms.ct.addTote")}
      okText={t("common.add")}
      cancelText={t("common.cancel")}
      confirmLoading={add.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" initialValues={{ count: kind === "cart" ? 1 : 10, slots: 12 }} onFinish={(v) => add.mutate(v)}>
        <Form.Item name="count" label={t("wms.ct.count")} extra={t(kind === "cart" ? "wms.ct.countHelpCart" : "wms.ct.countHelpTote")}>
          <InputNumber min={1} max={200} precision={0} style={{ width: 160 }} />
        </Form.Item>
        {kind === "cart" && (
          <Form.Item name="slots" label={t("wms.ct.slots")} extra={t("wms.ct.slotsHelp")}>
            <InputNumber min={1} max={100} precision={0} style={{ width: 160 }} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
